import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Divider,
    CircularProgress,
    Button
} from "@mui/material";
import dayjs from "dayjs";
import { Plus, AlertTriangle, Linkedin } from "lucide-react";
import config from "../../Config";
import * as Sentry from "@sentry/react";
import styles from "./UserPage.styles";
import { SimpleChatContainer } from "../../chat/components";

const InfoField = ({ label, value }) => (
    <Grid item xs={12} sm={6}>
        <Typography variant="body2" color="textSecondary" sx={styles.infoFieldLabel}>
            {label}
        </Typography>
        <Typography variant="subtitle1" sx={styles.infoFieldValue}>
            {value || "—"}
        </Typography>
    </Grid>
);

const UserProfile = () => {
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const userStr = localStorage.getItem("user");
        if (!userStr) {
            navigate("/login");
            return;
        }
        setUserData(JSON.parse(userStr));
    }, [navigate]);

    // Fetch user resources
    useEffect(() => {
        if (!userData?.token) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch accounts
                const accountsResponse = await fetch(`${config.apiBaseUrl}/account`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'user-authorization': userData.token,
                    },
                });

                if (accountsResponse.ok) {
                    const accountsData = await accountsResponse.json();
                    // Format account data consistently
                    const formattedAccounts = Array.isArray(accountsData) ?
                        accountsData.map((account) => ({
                            ...account,
                            created_at: account.created_at ? dayjs(account.created_at).format("YYYY-MM-DD HH:mm:ss") : "N/A",
                            modified_at: account.modified_at ? dayjs(account.modified_at).format("YYYY-MM-DD HH:mm:ss") : "N/A",
                        })) : [];
                    setAccounts(formattedAccounts);
                }

            } catch (error) {
                console.error("Error fetching user data:", error);
                Sentry.captureException(error);
                setError("Failed to load user data");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [userData]);

    if (loading || !userData) {
        return (
            <Layout>
                <Box sx={styles.loadingContainer}>
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    const {
        login,
        role,
        email,
        name,
        created_at,
        modified_at,
        last_login_at,
    } = userData.user_info || {};

    const formatDate = (date) => (date ? dayjs(date).format("YYYY-MM-DD HH:mm:ss") : "—");

    return (
        <Layout>
            <Box sx={styles.pageContainer}>
                {error && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#FFEBEE', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AlertTriangle color="#D32F2F" size={24} />
                        <Typography color="error.main">{error}</Typography>
                    </Box>
                )}

                <Grid container spacing={3} sx={{ height: 'calc(100vh - 2rem)' }}>
                    {/* User Information Section */}
                    <Grid item xs={12} md={6} sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 3,
                        height: '100%',
                        overflow: 'auto',
                        minHeight: 0  // Critical for flexbox overflow
                    }}>
                        <Card sx={styles.card}>
                            <CardContent sx={styles.cardContent}>
                                <Typography variant="h6" sx={styles.cardTitle}>User Information</Typography>
                                <Divider sx={styles.divider} />
                                <Grid container spacing={2}>
                                    <InfoField label="Login" value={login} />
                                    <InfoField label="Role" value={role} />
                                    <InfoField label="Email" value={email} />
                                    <InfoField label="Name" value={name} />
                                    <InfoField label="Created At" value={formatDate(created_at)} />
                                    <InfoField label="Modified At" value={formatDate(modified_at)} />
                                    <InfoField label="Last Login" value={formatDate(last_login_at)} />
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* LinkedIn Accounts Section */}
                        <Card sx={styles.card}>
                            <CardContent sx={styles.cardContent}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={styles.cardTitle}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Linkedin size={18} />
                                            LinkedIn Accounts
                                        </Box>
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => navigate('/accounts')}
                                        startIcon={<Plus size={16} />}
                                    >
                                        Add
                                    </Button>
                                </Box>
                                <Divider sx={styles.divider} />

                                {accounts.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 2 }}>
                                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                            No LinkedIn accounts connected
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            onClick={() => navigate('/accounts')}
                                            startIcon={<Plus size={16} />}
                                        >
                                            Connect Account
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr 1fr',
                                            borderBottom: '1px solid rgba(0,0,0,0.12)',
                                            py: 1
                                        }}>
                                            <Typography variant="subtitle2" fontWeight={600}>Account ID</Typography>
                                            <Typography variant="subtitle2" fontWeight={600}>Name</Typography>
                                            <Typography variant="subtitle2" fontWeight={600}>Profile URL</Typography>
                                        </Box>

                                        {accounts.map((account) => (
                                            <Box
                                                key={account.id}
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 1fr 1fr',
                                                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                                                    py: 2
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                        {account.id}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="body2">
                                                        {account.name || "N/A"}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="primary"
                                                        component="a"
                                                        href={account.url || "#"}
                                                        target="_blank"
                                                        sx={{ textDecoration: 'none' }}
                                                    >
                                                        {account.url ? "LinkedIn Profile" : "N/A"}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Shika AI Chat Section */}
                    <Grid item xs={12} md={6} sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        height: '100%',
                        overflow: 'hidden',
                        minHeight: 0  // Critical for flexbox overflow
                    }}>
                        <SimpleChatContainer />
                    </Grid>


                </Grid>
            </Box>
        </Layout>
    );
};

export default UserProfile;